import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, MapPin, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Switch } from "@/components/ui/switch";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------
function Field({ label, required = false, error, children, help, testid, className = "" }) {
  return (
    <div className={`space-y-1 ${className}`} data-testid={testid}>
      <Label className="text-[11px] uppercase tracking-widest text-neutral-500">
        {label}
        {required && <span className="text-red-500 ml-1" data-testid={testid ? `${testid}-required` : undefined}>*</span>}
      </Label>
      {children}
      {error && (
        <div className="text-[11px] text-red-600 font-medium"
              data-testid={testid ? `${testid}-error` : undefined}>{error}</div>
      )}
      {!error && help && <div className="text-[11px] text-neutral-500">{help}</div>}
    </div>
  );
}

function useLookups(category) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!category) return;
    api.get("/lookups", { params: { category, page: 1, page_size: 200 } })
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {});
  }, [category]);
  return items;
}

function useCollection(path, params = {}) {
  const [items, setItems] = useState([]);
  const stableParams = JSON.stringify(params);
  useEffect(() => {
    let mounted = true;
    api.get(path, { params: { page: 1, page_size: 200, ...JSON.parse(stableParams) } })
      .then(({ data }) => { if (mounted) setItems(data.items || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [path, stableParams]);
  return items;
}

/** GPS coord row with "Use my location" button. Two Number inputs + "Use my location" */
function GpsRow({ lat, lng, onChange, testidPrefix }) {
  const [busy, setBusy] = useState(false);
  const grab = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setBusy(false);
        toast.success(`GPS captured (±${Math.round(pos.coords.accuracy)}m)`);
      },
      (err) => { setBusy(false); toast.error("GPS: " + err.message); },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };
  return (
    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
      <Field label="Latitude">
        <Input type="number" step="any" value={lat ?? ""}
               onChange={(e) => onChange({ lat: e.target.value === "" ? null : Number(e.target.value), lng })}
               placeholder="23.0225" data-testid={`${testidPrefix}-lat`} />
      </Field>
      <Field label="Longitude">
        <Input type="number" step="any" value={lng ?? ""}
               onChange={(e) => onChange({ lat, lng: e.target.value === "" ? null : Number(e.target.value) })}
               placeholder="72.5714" data-testid={`${testidPrefix}-lng`} />
      </Field>
      <Button type="button" size="sm" variant="outline" onClick={grab} disabled={busy}
              data-testid={`${testidPrefix}-mylocation`} className="h-10">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4 mr-1" />}
        My location
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Distributor form (create + edit) — UAT B1 mandatory + B2 cascading geo + B3 multi-beat
// ---------------------------------------------------------------------------
export function DistributorForm({ open, onOpenChange, onSaved, existing = null }) {
  const paymentTerms = useLookups("payment_terms");
  const statuses = useLookups("distributor_status");
  const zones = useCollection("/zones");
  const linkedUsers = useCollection("/users", { role_code: "DISTRIBUTOR" });
  const allBeats = useCollection("/beats", { page_size: 2000 });
  // Polish Item 2 — reverse-lock: pre-load ALL cities/states/sub-zones so a
  // direct City pick can auto-fill and lock the upstream chain.
  const allCities = useCollection("/geography/cities");
  const allStates = useCollection("/geography/states");
  const allSubZones = useCollection("/geography/sub-zones");

  const empty = {
    code: "", name: "", contact_person: "", phone: "", email: "",
    gst_no: "", address: "",
    zone_id: "", state_id: "", sub_zone_id: "", city_id: "", area_id: "",
    beat_ids: [],
    gps_lat: null, gps_lng: null, credit_limit: 0,
    payment_terms_lookup_id: "", linked_user_id: "",
    status_lookup_id: "", notes: "",
    stock_visible_to_distributor: true,
  };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [filledFromUser, setFilledFromUser] = useState([]);
  const [showAllBeats, setShowAllBeats] = useState(false);
  const [beatSearch, setBeatSearch] = useState("");
  const isEdit = !!existing?.id;
  const [states, setStates] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  // Polish Item 2 — reverse-lock state. `true` means the upstream chain was
  // derived from a direct City pick and is locked read-only until City clears.
  const [lockedFromCity, setLockedFromCity] = useState(false);

  const patch = (k, v) => { setForm((s) => ({ ...s, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  // Cascading: reload downstream lists when upstream selection changes.
  useEffect(() => {
    if (!form.zone_id) { setStates([]); return; }
    api.get("/geography/states", { params: { zone_id: form.zone_id } })
      .then(({ data }) => setStates(data.items || data || []))
      .catch(() => setStates([]));
  }, [form.zone_id]);
  useEffect(() => {
    if (!form.state_id) { setSubZones([]); return; }
    api.get("/geography/sub-zones", { params: { state_id: form.state_id } })
      .then(({ data }) => setSubZones(data.items || data || []))
      .catch(() => setSubZones([]));
  }, [form.state_id]);
  useEffect(() => {
    if (!form.sub_zone_id) { setCities([]); return; }
    api.get("/geography/cities", { params: { sub_zone_id: form.sub_zone_id } })
      .then(({ data }) => setCities(data.items || data || []))
      .catch(() => setCities([]));
  }, [form.sub_zone_id]);
  useEffect(() => {
    if (!form.city_id) { setAreas([]); return; }
    api.get("/geography/areas", { params: { city_id: form.city_id } })
      .then(({ data }) => setAreas(data.items || data || []))
      .catch(() => setAreas([]));
  }, [form.city_id]);

  useEffect(() => {
    if (!open) { setForm(empty); setErrors({}); setLockedFromCity(false); return; }
    if (existing) {
      setForm({
        code: existing.code || "", name: existing.name || "",
        contact_person: existing.contact_person || "",
        phone: existing.phone || "", email: existing.email || "",
        gst_no: existing.gst_no || "", address: existing.address || "",
        zone_id: existing.zone_id || "", state_id: existing.state_id || "",
        sub_zone_id: existing.sub_zone_id || "",
        city_id: existing.city_id || "", area_id: existing.area_id || "",
        beat_ids: existing.beat_ids || [],
        gps_lat: existing.gps_lat ?? null, gps_lng: existing.gps_lng ?? null,
        credit_limit: existing.credit_limit ?? 0,
        payment_terms_lookup_id: existing.payment_terms_lookup_id || "",
        linked_user_id: existing.linked_user_id || "",
        status_lookup_id: existing.status_lookup_id || "",
        notes: existing.notes || "",
        stock_visible_to_distributor: existing.stock_visible_to_distributor !== false,
      });
    } else setForm(empty);
    setFilledFromUser([]);
    // Details still missing on the distributor are taken from its distributor login
    // (the DISTRIBUTOR-role user mapped to it), which usually already has them.
    if (existing?.id && (!existing.email || !existing.phone || !existing.linked_user_id)) {
      api.get(`/user-distributor-map/distributors/${existing.id}/users`, { params: { page_size: 200 } }).then(({ data }) => {
        const dus = (data.items || []).filter((m) => m.user_role_code === "DISTRIBUTOR");
        const me = (existing.name || "").trim().toLowerCase();
        const u = dus.find((m) => (m.user_name || "").trim().toLowerCase() === me) || (dus.length === 1 ? dus[0] : null);
        if (!u) return;
        const filled = [];
        if (!existing.email && u.user_email) filled.push("Email");
        if (!existing.phone && u.user_phone) filled.push("Phone");
        if (!existing.linked_user_id && u.user_id) filled.push("Linked user");
        if (!filled.length) return;
        setForm((f) => ({
          ...f,
          email: f.email || (!existing.email ? u.user_email || "" : f.email),
          phone: f.phone || (!existing.phone ? u.user_phone || "" : f.phone),
          linked_user_id: f.linked_user_id || (!existing.linked_user_id ? u.user_id || "" : f.linked_user_id),
        }));
        setFilledFromUser(filled);
      }).catch(() => {});
    }
    /* eslint-disable-next-line */
  }, [open, existing?.id]);

  // Client-side mandatory check — mirrors the server-side rules in
  // routes_distribution._validate_distributor_mandatory.
  const clientValidate = () => {
    const errs = {};
    const req = {
      code: "Code is required",
      name: "Name is required",
      phone: "Phone is required (E.164 format: +<country><number>)",
      email: "Email is required",
      zone_id: "Zone is required",
      state_id: "State is required",
      sub_zone_id: "Sub Zone is required",
      city_id: "City is required",
    };
    for (const [k, msg] of Object.entries(req)) {
      const v = form[k];
      if (v === null || v === undefined || String(v).trim() === "") errs[k] = msg;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      errs.email = "Email format is invalid";
    if (form.phone && !/^\+\d{7,15}$/.test(form.phone.trim()))
      errs.phone = "Phone must be E.164 (e.g. +919000000001)";
    return errs;
  };

  const canSubmit = Object.keys(clientValidate()).length === 0 && !busy;

  const scrollToFirstError = (errs) => {
    const first = ["code", "name", "phone", "email",
                   "zone_id", "state_id", "sub_zone_id", "city_id"]
      .find(k => errs[k]);
    if (!first) return;
    // eslint-disable-next-line no-undef
    const el = document.querySelector(`[data-testid="dist-form-${first.replace('_id','')}"]`);
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      const focusable = el.querySelector("input,button,select") || el;
      focusable.focus?.();
    }
  };

  const submit = async () => {
    const clientErrs = clientValidate();
    if (Object.keys(clientErrs).length) {
      setErrors(clientErrs);
      scrollToFirstError(clientErrs);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        credit_limit: Number(form.credit_limit) || 0,
        linked_user_id: form.linked_user_id || null,
        payment_terms_lookup_id: form.payment_terms_lookup_id || null,
        status_lookup_id: form.status_lookup_id || null,
      };
      if (isEdit) {
        const { code, ...rest } = payload;
        const { data } = await api.patch(`/distributors/${existing.id}`, rest);
        toast.success(`Distributor ${data.code} updated`);
      } else {
        const { data } = await api.post("/distributors", payload);
        toast.success(`Distributor ${data.code} created`);
      }
      onSaved?.(); onOpenChange(false);
    } catch (e) {
      // Server returned {detail: {field: msg}} — surface as inline errors.
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && !Array.isArray(d)) {
        setErrors(d);
        scrollToFirstError(d);
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  };

  const zoneOptions = zones.map(z => ({ id: z.id, code: z.code, name: z.name }));
  const stateOptions = states.map(s => ({ id: s.id, code: s.code, name: s.name }));
  const subZoneOptions = subZones.map(s => ({ id: s.id, code: s.code, name: s.name }));
  const cityOptions = cities.map(c => ({ id: c.id, code: "", name: c.name }));
  const areaOptions = areas.map(a => ({ id: a.id, code: "", name: a.name }));

  const beatPool = showAllBeats
    ? allBeats
    : allBeats.filter(b => (!form.zone_id || b.zone_id === form.zone_id) &&
                            (!form.area_id || b.area_id === form.area_id));
  const filteredBeats = beatPool.filter(b =>
    !beatSearch || (b.code || "").toLowerCase().includes(beatSearch.toLowerCase()) ||
                    (b.name || "").toLowerCase().includes(beatSearch.toLowerCase())
  );

  const toggleBeat = (id) => setForm(s => ({
    ...s,
    beat_ids: s.beat_ids.includes(id)
      ? s.beat_ids.filter(x => x !== id)
      : [...s.beat_ids, id]
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="distributor-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit distributor ${existing?.code || ""}` : "New distributor"}</DialogTitle>
          <DialogDescription>Wholesale partner with GPS, GST and payment terms.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Code" required error={errors.code} testid="dist-form-code-field">
            <Input value={form.code} disabled={isEdit}
                    onChange={(e) => patch("code", e.target.value.toUpperCase())}
                    data-testid="dist-form-code" />
          </Field>
          <Field label="Name" required error={errors.name} testid="dist-form-name-field">
            <Input value={form.name} onChange={(e) => patch("name", e.target.value)}
                    data-testid="dist-form-name" />
          </Field>
          {filledFromUser.length > 0 && (
            <div className="col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="dist-form-autofill-note">
              {filledFromUser.join(", ")} filled in from this distributor's user login. Check it, then press Save to keep it.
            </div>
          )}
          <Field label="Contact person" testid="dist-form-contact-field">
            <Input value={form.contact_person}
                    onChange={(e) => patch("contact_person", e.target.value)}
                    data-testid="dist-form-contact" />
          </Field>
          <Field label="Phone (E.164)" required error={errors.phone} testid="dist-form-phone-field">
            <Input value={form.phone} onChange={(e) => patch("phone", e.target.value)}
                    placeholder="+919000000000" data-testid="dist-form-phone" />
          </Field>
          <Field label="Email" required error={errors.email} testid="dist-form-email-field">
            <Input type="email" value={form.email}
                    onChange={(e) => patch("email", e.target.value)}
                    data-testid="dist-form-email" />
          </Field>
          <Field label="GST no. (15 chars)" testid="dist-form-gst-field">
            <Input value={form.gst_no}
                    onChange={(e) => patch("gst_no", e.target.value.toUpperCase())}
                    placeholder="24AAAAA0000A1Z5" data-testid="dist-form-gst" />
          </Field>

          {/* Polish Item 2 — Reverse-lock: quick city typeahead.
              Bypasses the top-down Zone→State→SubZone chain. Picking a city
              here derives + locks the upstream fields; clearing unlocks. */}
          <Field
            label="Quick pick City (reverse-lock)" className="col-span-2"
            help={lockedFromCity
              ? "Upstream Zone / State / Sub Zone are derived from City and locked. Clear to edit manually."
              : "Optional. Skip if you prefer top-down Zone → State → Sub Zone → City."}
            testid="dist-form-quickcity-field"
          >
            <SearchableSelect
              value={lockedFromCity ? form.city_id : ""}
              onChange={(cityId) => {
                if (!cityId) {
                  // Clear City → unlock upstream chain.
                  setLockedFromCity(false);
                  setForm(s => ({ ...s, city_id: "", area_id: "" }));
                  return;
                }
                const c = allCities.find(x => x.id === cityId);
                if (!c) return;
                const sz = allSubZones.find(x => x.id === c.sub_zone_id);
                const st = allStates.find(x => x.id === (sz?.state_id || c.state_id));
                setLockedFromCity(true);
                setForm(s => ({
                  ...s,
                  zone_id: st?.zone_id || "",
                  state_id: st?.id || c.state_id || "",
                  sub_zone_id: c.sub_zone_id || "",
                  city_id: c.id,
                  area_id: "",   // area is downstream — user still picks
                }));
                setErrors(e => ({ ...e, zone_id: undefined, state_id: undefined,
                                        sub_zone_id: undefined, city_id: undefined }));
              }}
              options={allCities.map(c => ({ id: c.id, code: "", name: c.name }))}
              placeholder="Type any city name (all 200+)…"
              testid="dist-form-quickcity"
            />
          </Field>

          {/* B2 — 5-level cascading geography */}
          <Field label="Zone" required error={errors.zone_id}
                 help={lockedFromCity ? "🔒 derived from City" : undefined}
                 testid="dist-form-zone-field">
            <SearchableSelect value={form.zone_id}
              onChange={(v) => setForm(s => ({ ...s, zone_id: v, state_id: "", sub_zone_id: "", city_id: "", area_id: "" }))}
              options={zoneOptions} placeholder="Search zone…"
              disabled={lockedFromCity}
              testid="dist-form-zone" />
          </Field>
          <Field label="State" required error={errors.state_id}
                 help={lockedFromCity ? "🔒 derived from City" : undefined}
                 testid="dist-form-state-field">
            <SearchableSelect value={form.state_id}
              onChange={(v) => setForm(s => ({ ...s, state_id: v, sub_zone_id: "", city_id: "", area_id: "" }))}
              options={stateOptions} disabled={lockedFromCity || !form.zone_id}
              placeholder={form.zone_id ? "Search state…" : "Select zone first"}
              testid="dist-form-state" />
          </Field>
          <Field label="Sub Zone" required error={errors.sub_zone_id}
                 help={lockedFromCity ? "🔒 derived from City" : undefined}
                 testid="dist-form-sub-zone-field">
            <SearchableSelect value={form.sub_zone_id}
              onChange={(v) => setForm(s => ({ ...s, sub_zone_id: v, city_id: "", area_id: "" }))}
              options={subZoneOptions} disabled={lockedFromCity || !form.state_id}
              placeholder={form.state_id ? "Search sub zone…" : "Select state first"}
              testid="dist-form-sub-zone" />
          </Field>
          <Field label="City" required error={errors.city_id} testid="dist-form-city-field">
            <SearchableSelect value={form.city_id}
              onChange={(v) => {
                // Manually clearing the City on the top-down field also
                // releases the reverse-lock so upstream fields become editable.
                if (!v && lockedFromCity) setLockedFromCity(false);
                setForm(s => ({ ...s, city_id: v, area_id: "" }));
              }}
              options={cityOptions} disabled={!form.sub_zone_id && !lockedFromCity}
              placeholder={form.sub_zone_id ? "Search city…" : (lockedFromCity ? "" : "Select sub zone first")}
              testid="dist-form-city" />
          </Field>
          {/* No Area field: a distributor IS its own area — the system keeps it in step. */}

          {/* B3 — multi-beat with chips */}
          <div className="col-span-2 rounded-lg border border-neutral-200 p-3" data-testid="dist-form-beats-block">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[11px] uppercase tracking-widest text-neutral-500">
                Beats <span className="text-neutral-400 normal-case">(optional)</span>
              </Label>
              <div className="flex items-center gap-3 text-xs">
                <label className="flex items-center gap-1 cursor-pointer" data-testid="dist-form-beats-show-all-label">
                  <input type="checkbox" checked={showAllBeats}
                         onChange={(e) => setShowAllBeats(e.target.checked)}
                         data-testid="dist-form-beats-show-all" />
                  Show all beats
                </label>
                <button type="button" className="underline"
                        onClick={() => setForm(s => ({ ...s, beat_ids: filteredBeats.map(b => b.id) }))}
                        data-testid="dist-form-beats-select-all">Select all visible</button>
                <button type="button" className="underline text-neutral-500"
                        onClick={() => patch("beat_ids", [])}
                        data-testid="dist-form-beats-clear-all">Clear all</button>
              </div>
            </div>
            {form.beat_ids.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2" data-testid="dist-form-beats-chips">
                {form.beat_ids.map(id => {
                  const b = allBeats.find(x => x.id === id) || { code: id.slice(0, 6) + "…" };
                  return (
                    <span key={id} className="inline-flex items-center gap-1 rounded-full bg-[#E5197F]/10 text-[#E5197F] px-2 py-0.5 text-xs" data-testid={`dist-form-beats-chip-${id}`}>
                      {b.code || b.name}
                      <button type="button" onClick={() => toggleBeat(id)} className="hover:opacity-70">×</button>
                    </span>
                  );
                })}
              </div>
            )}
            <Input value={beatSearch} onChange={(e) => setBeatSearch(e.target.value)}
                    placeholder="Search beat by code or name…"
                    data-testid="dist-form-beats-search" className="mb-2" />
            <div className="max-h-40 overflow-y-auto border border-neutral-200 rounded" data-testid="dist-form-beats-list">
              {filteredBeats.length === 0 ? (
                <div className="text-xs text-neutral-500 p-3">No beats match. Toggle "Show all beats" to widen.</div>
              ) : (
                filteredBeats.map(b => (
                  <label key={b.id} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-neutral-50 cursor-pointer"
                          data-testid={`dist-form-beats-row-${b.id}`}>
                    <input type="checkbox" checked={form.beat_ids.includes(b.id)}
                            onChange={() => toggleBeat(b.id)} />
                    <span className="font-mono">{b.code}</span>
                    <span className="text-neutral-500">— {b.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="col-span-2">
            <Field label="Address" testid="dist-form-address-field">
              <Textarea value={form.address} onChange={(e) => patch("address", e.target.value)} data-testid="dist-form-address" />
            </Field>
          </div>
          <div className="col-span-2">
            <GpsRow lat={form.gps_lat} lng={form.gps_lng}
                    onChange={({ lat, lng }) => setForm((s) => ({ ...s, gps_lat: lat, gps_lng: lng }))}
                    testidPrefix="dist-form-gps" />
          </div>
          <Field label="Credit limit (₹)" testid="dist-form-credit-field">
            <Input type="number" value={form.credit_limit} onChange={(e) => patch("credit_limit", e.target.value)} data-testid="dist-form-credit" />
          </Field>
          <Field label="Payment terms" testid="dist-form-terms-field">
            <Select value={form.payment_terms_lookup_id} onValueChange={(v) => patch("payment_terms_lookup_id", v)}>
              <SelectTrigger data-testid="dist-form-terms"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{paymentTerms.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Stock visible to distributor" testid="dist-form-stock-visible-field">
            <div className="flex items-center h-9 gap-2">
              <Switch checked={form.stock_visible_to_distributor}
                      onCheckedChange={(v) => patch("stock_visible_to_distributor", v)}
                      data-testid="dist-form-stock-visible" />
              <span className="text-xs text-neutral-500">
                {form.stock_visible_to_distributor ? "Enabled" : "Disabled"}
              </span>
            </div>
          </Field>
          <Field label="Linked distributor user" testid="dist-form-user-field">
            <Select value={form.linked_user_id || "__none__"}
                    onValueChange={(v) => patch("linked_user_id", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="dist-form-user"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {linkedUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name} — {u.phone || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status" testid="dist-form-status-field">
            <Select value={form.status_lookup_id} onValueChange={(v) => patch("status_lookup_id", v)}>
              <SelectTrigger data-testid="dist-form-status"><SelectValue placeholder="Active by default" /></SelectTrigger>
              <SelectContent>{statuses.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="col-span-2">
            <Field label="Notes" testid="dist-form-notes-field">
              <Textarea value={form.notes} onChange={(e) => patch("notes", e.target.value)} data-testid="dist-form-notes" />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit}
                  className="tulsi-gradient text-white font-semibold" data-testid="dist-form-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isEdit ? "Save changes" : "Create distributor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Retailer form (create + edit)
// ---------------------------------------------------------------------------
export function RetailerForm({ open, onOpenChange, onSaved, existing = null }) {
  const outletTypes = useLookups("outlet_type");
  const outletSizes = useLookups("outlet_size");
  const statuses = useLookups("outlet_status");
  const cities = useCollection("/geography/cities");
  const areas = useCollection("/geography/areas");
  const distributors = useCollection("/distributors", { page_size: 2000 });
  const beats = useCollection("/beats", { page_size: 200 });

  const empty = {
    code: "", name: "", owner_name: "", phone: "", email: "",
    address: "", city_id: "", area_id: "",
    distributor_id: "", beat_id: "",
    outlet_type_lookup_id: "", outlet_size_lookup_id: "",
    credit_days: 0, gst_no: "",
    gps_lat: null, gps_lng: null, status_lookup_id: "", notes: "",
    is_active: true,
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const isEdit = !!existing?.id;
  useEffect(() => {
    if (!open) { setForm(empty); return; }
    if (existing) {
      setForm({
        code: existing.code || "", name: existing.name || "",
        owner_name: existing.owner_name || "",
        phone: existing.phone || "", email: existing.email || "",
        address: existing.address || "",
        city_id: existing.city_id || "", area_id: existing.area_id || "",
        distributor_id: existing.distributor_id || "",
        beat_id: existing.beat_id || "",
        outlet_type_lookup_id: existing.outlet_type_lookup_id || "",
        outlet_size_lookup_id: existing.outlet_size_lookup_id || "",
        credit_days: existing.credit_days ?? 0,
        gst_no: existing.gst_no || "",
        gps_lat: existing.gps_lat ?? null,
        gps_lng: existing.gps_lng ?? null,
        status_lookup_id: existing.status_lookup_id || "",
        notes: existing.notes || "",
        is_active: existing.is_active !== false,
      });
    } else setForm(empty);
    /* eslint-disable-next-line */
  }, [open, existing?.id]);
  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        credit_days: Number(form.credit_days) || 0,
        beat_id: form.beat_id || null,
        outlet_type_lookup_id: form.outlet_type_lookup_id || null,
        outlet_size_lookup_id: form.outlet_size_lookup_id || null,
        status_lookup_id: form.status_lookup_id || null,
      };
      if (isEdit) {
        const { code, ...rest } = payload;
        const { data } = await api.patch(`/retailers/${existing.id}`, rest);
        toast.success(`Retailer ${data.code} updated`);
      } else {
        const { data } = await api.post("/retailers", payload);
        toast.success(`Retailer ${data.code} created`);
      }
      onSaved?.(); onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="retailer-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit retailer ${existing?.code || ""}` : "New retailer / outlet"}</DialogTitle>
          <DialogDescription>Store on a distributor's route with GPS captured by field SE.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Code"><Input value={form.code} disabled={isEdit} onChange={(e) => patch("code", e.target.value.toUpperCase())} data-testid="ret-form-code" /></Field>
          <Field label="Name"><Input value={form.name} onChange={(e) => patch("name", e.target.value)} data-testid="ret-form-name" /></Field>
          <Field label="Owner"><Input value={form.owner_name} onChange={(e) => patch("owner_name", e.target.value)} data-testid="ret-form-owner" /></Field>
          <Field label="Phone (E.164)"><Input value={form.phone} onChange={(e) => patch("phone", e.target.value)} data-testid="ret-form-phone" /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => patch("email", e.target.value)} data-testid="ret-form-email" /></Field>
          <Field label="GST no. (optional)"><Input value={form.gst_no} onChange={(e) => patch("gst_no", e.target.value.toUpperCase())} data-testid="ret-form-gst" /></Field>
          <Field label="City">
            <Select value={form.city_id} onValueChange={(v) => patch("city_id", v)}>
              <SelectTrigger data-testid="ret-form-city"><SelectValue placeholder="Select city…" /></SelectTrigger>
              <SelectContent>{cities.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Distributor">
            <Select value={form.distributor_id} onValueChange={(v) => patch("distributor_id", v)}>
              <SelectTrigger data-testid="ret-form-dist"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{distributors.map(d => <SelectItem key={d.id} value={d.id}>{d.code} — {d.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Beat (optional)">
            <Select value={form.beat_id || "__none__"} onValueChange={(v) => patch("beat_id", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="ret-form-beat"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {beats.map(b => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Outlet type">
            <Select value={form.outlet_type_lookup_id} onValueChange={(v) => patch("outlet_type_lookup_id", v)}>
              <SelectTrigger data-testid="ret-form-otype"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{outletTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Outlet size">
            <Select value={form.outlet_size_lookup_id} onValueChange={(v) => patch("outlet_size_lookup_id", v)}>
              <SelectTrigger data-testid="ret-form-osize"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{outletSizes.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Credit days"><Input type="number" value={form.credit_days} onChange={(e) => patch("credit_days", e.target.value)} data-testid="ret-form-credit" /></Field>
          <Field label="Active"
                 help="Inactive outlets are hidden from the SE's Today's Beat and every order/visit flow.">
            <Select value={form.is_active ? "active" : "inactive"}
                    onValueChange={(v) => patch("is_active", v === "active")}>
              <SelectTrigger data-testid="ret-form-active"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Status label (optional)"
                 help="A descriptive tag only — does not affect visibility. Use Active/Inactive above for that.">
            <Select value={form.status_lookup_id} onValueChange={(v) => patch("status_lookup_id", v)}>
              <SelectTrigger data-testid="ret-form-status"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{statuses.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="col-span-2">
            <Field label="Address">
              <Textarea value={form.address} onChange={(e) => patch("address", e.target.value)} data-testid="ret-form-address" />
            </Field>
          </div>
          <div className="col-span-2">
            <GpsRow lat={form.gps_lat} lng={form.gps_lng}
                    onChange={({ lat, lng }) => setForm((s) => ({ ...s, gps_lat: lat, gps_lng: lng }))}
                    testidPrefix="ret-form-gps" />
          </div>
          <div className="col-span-2">
            <Field label="Notes"><Textarea value={form.notes} onChange={(e) => patch("notes", e.target.value)} data-testid="ret-form-notes" /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit}
                  disabled={busy || !form.code || !form.name || !form.distributor_id}
                  className="tulsi-gradient text-white font-semibold" data-testid="ret-form-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isEdit ? "Save changes" : "Create retailer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Beat form (create + edit)
// ---------------------------------------------------------------------------
export function BeatForm({ open, onOpenChange, onSaved, existing = null }) {
  const dayLookups = useLookups("day_of_week");
  const statuses = useLookups("beat_status");
  const distributors = useCollection("/distributors", { page_size: 2000 });
  const ses = useCollection("/users", { role_code: "SE", page_size: 200 });

  const empty = {
    code: "", name: "", distributor_id: "",
    assigned_se_user_id: "", day_of_week: "",
    status_lookup_id: "", notes: "",
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const isEdit = !!existing?.id;
  useEffect(() => {
    if (!open) { setForm(empty); return; }
    if (existing) {
      setForm({
        code: existing.code || "", name: existing.name || "",
        distributor_id: "",           // filled below once the distributor list is loaded
        assigned_se_user_id: existing.assigned_se_user_id || "",
        day_of_week: existing.day_of_week || "",
        status_lookup_id: existing.status_lookup_id || "",
        notes: existing.notes || "",
      });
    } else setForm(empty);
    /* eslint-disable-next-line */
  }, [open, existing?.id]);
  // The distributor serving a beat = the one listing it (a beat belongs to exactly one).
  useEffect(() => {
    if (!open || !existing?.id || form.distributor_id || distributors.length === 0) return;
    const d = distributors.find(x => (x.beat_ids || []).includes(existing.id))
      || distributors.find(x => x.area_id && x.area_id === existing.area_id);
    if (d) setForm(s => ({ ...s, distributor_id: d.id }));
    /* eslint-disable-next-line */
  }, [open, existing?.id, distributors]);
  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        distributor_id: form.distributor_id || undefined,
        assigned_se_user_id: form.assigned_se_user_id || null,
        day_of_week: form.day_of_week || null,
        status_lookup_id: form.status_lookup_id || null,
      };
      if (isEdit) {
        const { code, ...rest } = payload;
        const { data } = await api.patch(`/beats/${existing.id}`, rest);
        toast.success(`Beat ${data.code} updated`);
      } else {
        const { data } = await api.post("/beats", payload);
        toast.success(`Beat ${data.code} created`);
      }
      onSaved?.(); onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="beat-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit beat ${existing?.code || ""}` : "New beat / route"}</DialogTitle>
          <DialogDescription>A route (beat) of a distributor. Users mapped to that distributor see it automatically.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Code">
            <Input value={isEdit ? form.code : ""} disabled placeholder={isEdit ? "" : "Auto-generated"} data-testid="beat-form-code" />
          </Field>
          <Field label="Name"><Input value={form.name} onChange={(e) => patch("name", e.target.value)} data-testid="beat-form-name" /></Field>
          {/* Only the distributor is set here. Users see the beat because the distributor is
              mapped to them (Users → Distributor mapping) — no per-user assignment needed. */}
          <Field label="Distributor" required className="col-span-2">
            <SearchableSelect value={form.distributor_id} onChange={(v) => patch("distributor_id", v)}
              options={distributors.map(d => ({ id: d.id, label: `${d.code} — ${d.name}` }))}
              placeholder="Search distributor…" clearable={false} testid="beat-form-distributor" />
          </Field>
          <Field label="Status">
            <Select value={form.status_lookup_id} onValueChange={(v) => patch("status_lookup_id", v)}>
              <SelectTrigger data-testid="beat-form-status"><SelectValue placeholder="Active by default" /></SelectTrigger>
              <SelectContent>{statuses.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="col-span-2">
            <Field label="Notes"><Textarea value={form.notes} onChange={(e) => patch("notes", e.target.value)} data-testid="beat-form-notes" /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.name || (!isEdit && !form.distributor_id)}
                  className="tulsi-gradient text-white font-semibold" data-testid="beat-form-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isEdit ? "Save changes" : "Create beat"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
